import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, CalendarClock, CheckCircle2, Loader2, RefreshCw, Sparkles, Stamp } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { Button } from 'components/ui/button'
import { FloatingPanel } from 'components/ui/floating-panel'
import type { Point } from 'components/ui/floatingGeometry'
import { cn } from 'lib/utils'

import { getBriefing, type Briefing, type BriefingHighlightKind } from '../services/briefingService'

/**
 * The Daily Briefing (MYD-638, MYD-644): a headline and up to four highlights
 * about the user's day, each linking to its record.
 *
 * Opened from "Daily Briefing" at the left of the Home header, as a floating
 * popup that can be dragged anywhere on screen. It used to be a card at the
 * top of Home; as a popup it is there when wanted and out of the way when not.
 *
 * The server keeps one briefing per user per day, so opening it is usually a
 * cache read. Every link is the server's, set from the record a highlight is
 * about.
 */

const ICONS: Record<BriefingHighlightKind, LucideIcon> = {
	approval: Stamp,
	overdue: AlertCircle,
	dueSoon: CalendarClock,
	activity: CheckCircle2,
}

const TONES: Record<BriefingHighlightKind, string> = {
	approval: 'text-primary',
	overdue: 'text-destructive',
	dueSoon: 'text-warning',
	activity: 'text-gray-400',
}

export const BRIEFING_TITLE = 'Daily Briefing'

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

/** The briefing's content: fetched when shown, refreshable. */
export function BriefingBody({ load = getBriefing }: { load?: typeof getBriefing }) {
	const [briefing, setBriefing] = useState<Briefing | null>(null)
	const [loading, setLoading] = useState(true)
	const [refreshing, setRefreshing] = useState(false)
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		let cancelled = false
		load()
			.then(data => !cancelled && setBriefing(data))
			.catch(() => !cancelled && setFailed(true))
			.finally(() => !cancelled && setLoading(false))
		return () => {
			cancelled = true
		}
	}, [load])

	const refresh = useCallback(async () => {
		setRefreshing(true)
		try {
			setBriefing(await load({ refresh: true }))
			setFailed(false)
		} catch {
			// Keep what is shown; a failed refresh is not worth an error.
		} finally {
			setRefreshing(false)
		}
	}, [load])

	if (loading) {
		return (
			<p className="flex items-center gap-2 p-4 text-sm text-gray-500" role="status">
				<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
				Preparing your briefing…
			</p>
		)
	}

	if (failed || !briefing) {
		return (
			<p className="p-4 text-sm text-gray-500" role="alert">
				Your briefing isn’t available just now. Try again in a moment.
			</p>
		)
	}

	return (
		<div className="p-4">
			<p className="text-sm font-semibold text-gray-900">{briefing.headline}</p>
			{briefing.highlights.length > 0 && (
				<ul className="mt-3 space-y-2">
					{briefing.highlights.map((item, n) => {
						const Icon = ICONS[item.kind] ?? CheckCircle2
						return (
							<li key={n} className="flex items-start gap-2 text-sm text-gray-700">
								<Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', TONES[item.kind] ?? 'text-gray-400')} aria-hidden="true" />
								{item.link ? (
									<Link to={item.link} className="text-gray-700 hover:text-primary hover:underline">
										{item.text}
									</Link>
								) : (
									<span>{item.text}</span>
								)}
							</li>
						)
					})}
				</ul>
			)}
			<div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
				<span>As of {timeOf(briefing.generatedAt)}</span>
				<Button type="button" variant="ghost" size="input" onClick={refresh} disabled={refreshing} aria-label="Refresh briefing">
					<RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} aria-hidden="true" />
				</Button>
			</div>
		</div>
	)
}

/** The blue AI icon and "Daily Briefing", in the same blue, as one label. */
const TitleMark = ({ iconClassName = 'h-4 w-4', wordsClassName }: { iconClassName?: string; wordsClassName?: string }) => (
	<span className="inline-flex items-center gap-1.5 text-primary">
		<Sparkles className={cn('shrink-0', iconClassName)} aria-hidden="true" />
		<span className={wordsClassName}>{BRIEFING_TITLE}</span>
	</span>
)

/** The briefing as a floating, draggable popup. */
export function DailyBriefingPopup({ open, onClose, anchor, load }: { open: boolean; onClose: () => void; anchor?: () => Point | null; load?: typeof getBriefing }) {
	return (
		<FloatingPanel open={open} onClose={onClose} title={<TitleMark />} ariaLabel={BRIEFING_TITLE} closeLabel="Close the daily briefing" width={420} anchor={anchor} data-testid="daily-briefing">
			<BriefingBody load={load} />
		</FloatingPanel>
	)
}

/**
 * "Daily Briefing" in the Home header: the blue AI icon and the words in the
 * same blue. On a phone the words give way to the icon, which keeps its name.
 */
export const DailyBriefingTrigger = forwardRef<HTMLButtonElement, { open: boolean; onToggle: () => void }>(({ open, onToggle }, ref) => (
	<button
		ref={ref}
		type="button"
		onClick={onToggle}
		aria-label={BRIEFING_TITLE}
		aria-expanded={open}
		data-testid="daily-briefing-trigger"
		className={cn(
			'inline-flex h-9 items-center rounded-md px-2 text-sm font-semibold transition-colors hover:bg-primary/5',
			'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
			open && 'bg-primary/10',
		)}
	>
		<TitleMark iconClassName="h-5 w-5" wordsClassName="hidden sm:inline" />
	</button>
))
DailyBriefingTrigger.displayName = 'DailyBriefingTrigger'
